import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, MessageCircle, Send, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { PublicAssistantResponse } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { api, errorMessage, HttpError } from '@/lib/api';

const FAQ = ['Quais serviços vocês oferecem e quanto custam?', 'Qual o horário de funcionamento?', 'Onde fica a barbearia?', 'Tem horário livre amanhã?'];

interface Msg {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Botão flutuante com perguntas frequentes. Consulta a disponibilidade do assistente uma única vez;
 * se a IA não estiver configurada (503) ou o site não existir, o widget não aparece.
 */
export function PublicAssistantWidget({ slug }: { slug: string }) {
  const probe = useQuery({
    queryKey: ['public-assistant', slug],
    queryFn: async () => {
      try {
        await api.get(`/public/${slug}/assistant`);
        return true;
      } catch (e) {
        if (e instanceof HttpError) return false;
        return false;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  if (!probe.data) return null;
  return <Panel slug={slug} />;
}

function Panel({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [messages, setMessages] = useState<Msg[]>([]);
  const endRef = useRef<HTMLDivElement>(null);

  const send = useMutation({
    mutationFn: (history: Msg[]) => api.post<PublicAssistantResponse>(`/public/${slug}/assistant`, { messages: history.slice(-8) }),
    onSuccess: (r) => setMessages((m) => [...m, { role: 'assistant', content: r.reply }]),
    onError: (e) =>
      setMessages((m) => [...m, { role: 'assistant', content: `${errorMessage(e)} Você também pode agendar direto pela página de agendamento.` }]),
  });

  useEffect(() => endRef.current?.scrollIntoView?.({ block: 'end' }), [messages, send.isPending, open]);

  const ask = (content: string) => {
    const value = content.trim().slice(0, 500);
    if (!value || send.isPending) return;
    const next = [...messages, { role: 'user' as const, content: value }];
    setMessages(next);
    setText('');
    send.mutate(next);
  };

  return (
    <div className="fixed right-4 bottom-4 z-40 flex flex-col items-end gap-3">
      {open && (
        <section
          role="dialog"
          aria-label="Perguntas sobre a barbearia"
          className="flex h-[min(32rem,calc(100dvh-6rem))] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg border bg-background text-foreground shadow-lg"
        >
          <header className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-sm font-semibold">Dúvidas rápidas</h2>
            <Button variant="ghost" size="icon" aria-label="Fechar painel" onClick={() => setOpen(false)}>
              <X className="size-4" aria-hidden />
            </Button>
          </header>
          <div className="flex-1 space-y-3 overflow-y-auto p-4 text-sm" aria-live="polite">
            {messages.length === 0 && (
              <>
                <p className="text-muted-foreground">Escolha uma pergunta ou escreva a sua. Para marcar horário, use a página de agendamento.</p>
                <div className="grid gap-2">
                  {FAQ.map((q) => (
                    <Button key={q} variant="outline" size="sm" className="h-auto justify-start whitespace-normal py-2 text-left" onClick={() => ask(q)}>
                      {q}
                    </Button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <p className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>{m.content}</p>
              </div>
            ))}
            {send.isPending && (
              <p role="status" className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden /> Consultando…
              </p>
            )}
            <div ref={endRef} />
          </div>
          <form
            className="flex items-center gap-2 border-t p-3"
            onSubmit={(e) => {
              e.preventDefault();
              ask(text);
            }}
          >
            <label htmlFor="pub-ai-input" className="sr-only">
              Sua pergunta
            </label>
            <input
              id="pub-ai-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={500}
              placeholder="Escreva sua pergunta"
              className="h-9 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
            <Button type="submit" size="icon" aria-label="Enviar pergunta" disabled={send.isPending || !text.trim()}>
              <Send className="size-4" aria-hidden />
            </Button>
          </form>
        </section>
      )}
      <Button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'Fechar dúvidas rápidas' : 'Abrir dúvidas rápidas'}
        size="icon"
        className="size-12 rounded-full shadow-md"
      >
        <MessageCircle className="size-5" aria-hidden />
      </Button>
    </div>
  );
}
